import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ActivitySquare, Building2, ChefHat, LogOut, Menu, Shield, Smartphone, UserCircle, Users } from 'lucide-react';
import { useAuthStore } from '../store/authStore';

const navItems = [
  { icon: Building2, label: 'Visao geral', to: '/platform/visao-geral', subtitle: 'Indicadores da base' },
  { icon: ActivitySquare, label: 'Estabilidade', to: '/platform/estabilidade', subtitle: 'Saude tecnica dos tenants' },
  { icon: Smartphone, label: 'Bridge Android', to: '/platform/bridge', subtitle: 'Releases do app' },
  { icon: Shield, label: 'Restaurantes', to: '/platform/restaurantes', subtitle: 'Tenants e assinaturas' },
  { icon: Users, label: 'Usuarios', to: '/platform/usuarios', subtitle: 'Acessos da plataforma' },
];

const PlatformLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const activeNavItem = navItems.find((item) => location.pathname.startsWith(item.to)) ?? navItems[0];

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const SidebarContent = () => (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-[#2A2A2A] px-4 py-5">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-hover shadow-glow-sm">
          <Shield className="h-5 w-5 text-white" />
        </div>
        <div>
          <span className="text-base font-bold text-white tracking-tight">Garçom Digital</span>
          <p className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">Platform</p>
        </div>
      </div>

      <nav className="flex-1 space-y-1 p-3">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
          >
            <item.icon className="h-5 w-5 flex-shrink-0" />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-[#2A2A2A] p-3">
        <div className="group flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-[#1A1A1A]">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/20">
            <UserCircle className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-white">{user?.name ?? 'Platform Owner'}</p>
            <p className="truncate text-[11px] text-muted-foreground">super admin</p>
          </div>
          <button onClick={handleLogout} className="p-1 transition-opacity group-hover:text-destructive" title="Sair">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <aside className="hidden w-64 flex-col border-r border-[#2A2A2A] bg-surface lg:flex">
        <SidebarContent />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="relative z-10 flex w-72 flex-col border-r border-[#2A2A2A] bg-surface">
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-16 items-center justify-between border-b border-[#2A2A2A] bg-surface px-4 md:px-6">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileOpen(true)} className="rounded-lg p-2 text-muted-foreground transition-all hover:bg-[#1A1A1A] hover:text-white lg:hidden">
              <Menu className="h-5 w-5" />
            </button>
            <div>
              <p className="text-sm font-semibold text-white">{activeNavItem.label}</p>
              <p className="text-xs text-muted-foreground">{activeNavItem.subtitle}</p>
            </div>
          </div>
          <div className="hidden items-center gap-2 rounded-xl bg-[#121212] px-3 py-2 md:flex">
            <ChefHat className="h-4 w-4 text-primary" />
            <span className="text-sm text-white">Super Admin</span>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-6">{children}</main>
      </div>
    </div>
  );
};

export default PlatformLayout;
